import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Calculator, TrendingUp } from "lucide-react";

const DEFAULTS = {
  home_net_monthly: 62250,
  fx_to_usd: 3.6725,
  home_housing_norm: 54168,
  host_housing_cost: 24000,
  net_retention_factor: 0.70284,
  mobility_protection_pct: 0.05,
  incentive_pct: 0.30,
  pct_401k: 0.04,
  medical_premium: 6850,
  home_currency: "AED",
  home_stated_in_usd: false,
};

const usd = (v) =>
  (v < 0 ? "−$" : "$") + Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 });

function Field({ label, name, value, onChange, step = "any", suffix }) {
  return (
    <div>
      <Label className="term-label text-slate-600">{label}</Label>
      <div className="relative mt-1">
        <Input
          type="number"
          step={step}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          className="font-mono-fin text-sm bg-white border-emerald-200 focus-visible:ring-emerald-600"
          data-testid={`usm-input-${name}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>
        )}
      </div>
    </div>
  );
}

function StatementRow({ ln }) {
  const { kind, label, value, sign } = ln;
  if (kind === "pct") {
    return (
      <div className="flex items-center justify-between px-5 py-2 border-t border-emerald-100/70">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="font-mono-fin text-sm font-semibold text-emerald-800">{value}%</span>
      </div>
    );
  }
  if (kind === "input") {
    return (
      <div className="flex items-center justify-between px-5 py-2 border-t border-emerald-100/70">
        <span className="text-sm text-slate-500">{label}</span>
        <span className="font-mono-fin text-sm text-slate-500">{value.toLocaleString("en-US")}</span>
      </div>
    );
  }
  const isTotal = kind === "total";
  const isSub = kind === "subtotal";
  const color = sign === "-" ? "text-rose-600" : sign === "+" ? "text-emerald-700" : "text-slate-800";
  return (
    <div
      className={`flex items-center justify-between px-5 ${
        isTotal
          ? "py-3 bg-[#C9A227]/15 border-y-2 border-[#C9A227]/50"
          : isSub
          ? "py-2.5 bg-emerald-50/70 border-t border-emerald-200"
          : "py-2 border-t border-emerald-100/70"
      }`}
    >
      <span
        className={`text-sm ${isTotal ? "font-bold uppercase tracking-wide text-[#8a6d0f]" : isSub ? "font-semibold text-emerald-900" : "text-slate-700"}`}
      >
        {sign && <span className={`mr-1.5 font-mono ${sign === "+" ? "text-emerald-600" : "text-rose-500"}`}>{sign}</span>}
        {label}
      </span>
      <span
        className={`font-mono-fin tabular text-right ${isTotal || isSub ? "text-base font-extrabold" : "text-sm"} ${isTotal ? "text-[#8a6d0f]" : color}`}
      >
        {usd(value)}
      </span>
    </div>
  );
}

export function USSalaryModelTab() {
  const [inp, setInp] = useState(DEFAULTS);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback((payload) => {
    setLoading(true);
    api
      .post("/us-salary-model", payload)
      .then((r) => setRes(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const payload = { ...inp };
      ["home_net_monthly", "fx_to_usd", "home_housing_norm", "host_housing_cost", "net_retention_factor", "mobility_protection_pct", "incentive_pct", "pct_401k", "medical_premium"].forEach(
        (k) => (payload[k] = parseFloat(payload[k]) || 0)
      );
      run(payload);
    }, 350);
    return () => clearTimeout(t);
  }, [inp, run]);

  const onChange = (name, value) => setInp((s) => ({ ...s, [name]: value }));
  const s = res?.summary;

  return (
    <div className="grid lg:grid-cols-5 gap-6 mt-4" data-testid="us-salary-model">
      {/* Inputs */}
      <Card className="lg:col-span-2 p-5 bg-white border-emerald-200">
        <div className="flex items-center gap-2 mb-1">
          <Calculator className="w-4 h-4 text-emerald-700" />
          <h3 className="font-display font-bold text-emerald-900">US Salary Model — Inputs</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Net-to-base retention method (ESSAR Metals &amp; Mining). Defaults reproduce the Praloy case, Dubai → Des Moines.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Home net — monthly" name="home_net_monthly" value={inp.home_net_monthly} onChange={onChange} />
          <Field label={`FX (${inp.home_currency}=1 USD)`} name="fx_to_usd" value={inp.fx_to_usd} onChange={onChange} />
          <Field label="Home housing norm" name="home_housing_norm" value={inp.home_housing_norm} onChange={onChange} />
          <Field label="Host housing cost" name="host_housing_cost" value={inp.host_housing_cost} onChange={onChange} />
          <Field label="Net-retention factor" name="net_retention_factor" value={inp.net_retention_factor} onChange={onChange} />
          <Field label="Mobility premium" name="mobility_protection_pct" value={inp.mobility_protection_pct} onChange={onChange} suffix="frac" />
          <Field label="Incentive %" name="incentive_pct" value={inp.incentive_pct} onChange={onChange} suffix="frac" />
          <Field label="401(k) %" name="pct_401k" value={inp.pct_401k} onChange={onChange} suffix="frac" />
          <Field label="Medical premium" name="medical_premium" value={inp.medical_premium} onChange={onChange} />
          <div className="flex items-center justify-between rounded-md border border-emerald-200 px-3 py-2">
            <Label className="term-label text-slate-600">Home already in USD</Label>
            <Switch
              checked={inp.home_stated_in_usd}
              onCheckedChange={(v) => onChange("home_stated_in_usd", v)}
              data-testid="usm-input-home_stated_in_usd"
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
          US 2026 tax: MFJ federal brackets (Rev. Proc. 2025-32), $32,200 standard deduction, Iowa flat 3.8%, Social Security 6.2% to $184,500, Medicare 1.45% + 0.9% over $250k. Medical premium is pre-tax &amp; pre-FICA; 401(k) is pre-income-tax only.
        </p>
      </Card>

      {/* Statement */}
      <Card className="lg:col-span-3 p-0 overflow-hidden bg-white border-emerald-200">
        <div className="bg-[#0B3D2E] px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] text-[#C9A227] font-semibold">ESSAR METALS &amp; MINING</div>
            <div className="text-white font-display font-bold">Current Compensation → US Salary</div>
          </div>
          {loading && <Loader2 className="w-4 h-4 text-[#C9A227] animate-spin" />}
        </div>

        {s && (
          <div className="grid grid-cols-3 gap-px bg-emerald-100 border-b border-emerald-200">
            <div className="bg-white px-4 py-3">
              <div className="term-label text-slate-500">Annual Base</div>
              <div className="font-mono-fin font-extrabold text-lg text-emerald-900" data-testid="usm-base">{usd(s.annual_base_salary)}</div>
            </div>
            <div className="bg-white px-4 py-3">
              <div className="term-label text-slate-500">Host Spendable</div>
              <div className="font-mono-fin font-extrabold text-lg text-emerald-900" data-testid="usm-spendable">{usd(s.host_spendable)}</div>
            </div>
            <div className="bg-white px-4 py-3">
              <div className="term-label text-slate-500 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-[#C9A227]" />Saving Capacity</div>
              <div className="font-mono-fin font-extrabold text-lg text-[#8a6d0f]" data-testid="usm-saving">{usd(s.annual_saving_capacity)}</div>
            </div>
          </div>
        )}

        {res && (
          <div className="divide-y-0" data-testid="usm-statement">
            {res.lines.map((ln, i) => (
              <StatementRow key={i} ln={ln} />
            ))}
          </div>
        )}

        {res && (
          <div className="px-5 py-3 border-t border-emerald-100 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-emerald-50/40">
            {[
              ["Federal", res.tax_detail.federal],
              ["Iowa (3.8%)", res.tax_detail.state],
              ["Social Security", res.tax_detail.social_security],
              ["Medicare", res.tax_detail.medicare + res.tax_detail.additional_medicare],
            ].map(([l, v]) => (
              <div key={l}>
                <div className="term-label text-slate-500">{l}</div>
                <div className="font-mono-fin text-sm text-rose-600">{usd(v)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
